import { useState } from 'react';
import { createPortal } from 'react-dom';
import { doc, setDoc } from 'firebase/firestore';
import { useT } from '@/i18n/react';
import { useAuth } from '@/hooks/AuthProvider';
import { useProfile } from '@/hooks/ProfileProvider';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui';
import { CellColorDial } from '@/components/CellColorDial';
import { DEFAULT_CELL_COLOR, isCellColor, type CellColor } from '@/lib/cellColors';
import { markOnboardingDone, type OnboardingState } from '@/lib/onboarding';
import './PrivacyZoneOnboardingSheet.css';

/**
 * Whether the next recording start should show the cell colour picker (GRUNDO
 * #105, Geri): ONCE for every user — new and existing alike, chosen colour or
 * not — so everyone learns that the colour can be picked. Until the settings
 * document has answered (offline, first load) it never blocks the start.
 */
export function needsCellColorChoice(onboarding: OnboardingState): boolean {
  return onboarding.loaded && onboarding.cellColorAt === null;
}

/**
 * The cell colour picker before an activity. "Start" saves the shown colour
 * (the current one if untouched) and marks the picker as seen
 * (`private/settings.onboarding.cellColorAt`), so it never comes back. A failed
 * write does not block the start: the colour is cosmetic.
 */
export function CellColorOnboardingSheet({ onComplete }: { onComplete: () => void }) {
  const t = useT().cellColorOnboarding;
  const { user } = useAuth();
  const { profile, patchProfile } = useProfile();
  const [color, setColor] = useState<CellColor>(isCellColor(profile?.cellColor) ? profile.cellColor : DEFAULT_CELL_COLOR);
  const [saving, setSaving] = useState(false);

  async function confirm() {
    setSaving(true);
    try {
      if (user && db) {
        await Promise.all([
          setDoc(doc(db, 'users', user.uid), { cellColor: color, updatedAt: new Date() }, { merge: true }),
          markOnboardingDone(user.uid, 'cellColorAt'),
        ]);
        patchProfile({ cellColor: color });
      }
    } catch {
      // Cosmetic choice: never block the first activity on it.
    } finally {
      setSaving(false);
      onComplete();
    }
  }

  return createPortal(
    <div className="pzgate" role="dialog" aria-modal="true" aria-label={t.title}>
      <div className="pzgate__sheet ccgate">
        <h1 className="pzgate__title">{t.title}</h1>
        <CellColorDial active={color} onChoose={setColor} />
        <div className="pzgate__actions">
          <Button block loading={saving} onClick={() => void confirm()}>
            {t.start}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
